/**
 * Site-rules bundle — schema + bundled baseline (P1 of the remote-site-rules
 * design, `docs/superpowers/specs/2026-08-26-remote-site-rules-and-pmr-design.md`).
 *
 * The goal of the whole effort is to move per-site autofill knowledge out of the
 * extension bundle into signed, remotely-fetched data so a site fix is a publish,
 * not a release. This file is **P1**: the schema plus a `BASELINE_SITE_RULES`
 * bundle built from today's hand-vetted `RELATED_DOMAIN_GROUPS`, plus pure
 * accessors. It is **additive and behavior-neutral** — nothing consumes it yet;
 * `relatedDomains.ts` is untouched. P2 wires the extension's matcher onto the
 * bundle-aware accessors here (sourced from a fetched bundle, falling back to
 * this baseline); P3 populates it from Apple's dataset.
 *
 * Pure data + pure functions — no crypto, no I/O — so every client can consume it.
 */
import { RELATED_DOMAIN_GROUPS } from './relatedDomains';

export const SITE_RULES_FORMAT = 'heybesafe-site-rules';
export const SITE_RULES_VERSION = 1;

/**
 * Closed set of per-site fill quirks. Clients switch on known values and IGNORE
 * unknown ones (forward-compatible: a newer bundle can add a quirk without
 * breaking older clients). This union is an authoring aid; the runtime treats an
 * incoming quirk string as untrusted data.
 */
export type SiteQuirk = 'no-autofill' | 'fill-on-user-gesture' | 'no-iframe-fill' | 'simulate-typing';

/** Forced form classification for a site. */
export type SiteForm = 'login' | 'signup' | 'change-password';

/** A set of registrable domains treated as the same login system. */
export interface EquivalentGroup {
  /** Registrable domains (eTLD+1), lowercase. */
  group: string[];
  note?: string;
  /**
   * Hand-vetted for anti-phishing. ONLY `vetted` groups are ever applied to
   * password-login matching, and never to passkeys (the caller enforces the
   * passkey exclusion, exactly as today). Unvetted groups (e.g. raw Apple
   * shared-credentials candidates, P3) are carried but never matched.
   */
  vetted: boolean;
}

/**
 * The closed set of card-field roles a PSP frame can resolve to. CVV is a role a
 * frame can *declare* it holds, but the background NEVER dispatches a security
 * code to any frame (spec §3.4 / §4.1) — the role exists so the number/exp/etc.
 * frames can be told apart from the cvv frame, not so the cvv is ever filled.
 */
export type PspRole = 'number' | 'exp' | 'cvv' | 'brand' | 'cardholder';

/** The closed PspRole set as data, for validating an untrusted bundle string. */
const PSP_ROLES: ReadonlySet<string> = new Set<PspRole>([
  'number', 'exp', 'cvv', 'brand', 'cardholder',
]);

/**
 * How a PSP frame declares which card field it holds, by URL query parameter.
 * Gr4vy encodes the role in the frame URL
 * (`…/input.html?type=number|expiryDate|securityCode`). `map` values are treated
 * as untrusted data: an unknown value resolves to `null`, never to code.
 */
export interface PspRoleByQueryParam {
  /** The query-param name carrying the field kind, e.g. `'type'`. */
  byQueryParam: string;
  /** Raw query-param value → canonical role, e.g. `{ number: 'number', expiryDate: 'exp' }`. */
  map: Record<string, PspRole>;
}

/**
 * How a PSP frame declares which card field it holds, by its iframe `title`.
 * Stripe Elements does NOT encode the role in the frame URL — every field frame
 * is served from `https://js.stripe.com` — but the merchant page's `<iframe>`
 * carries a human-readable `title` (e.g. `"Secure card number input frame"`).
 * `map` keys are **lowercased substrings** tested against the (lowercased) title;
 * the FIRST key that is a case-insensitive substring of the title wins, mapping
 * to its role. A single combined Card/Payment Element frame (title contains
 * `"card payment"` / `"payment input"`) maps to the `number` anchor. `map` values
 * are untrusted data: an unknown value resolves to `null`, never to code.
 */
export interface PspRoleByFrameTitle {
  byFrameTitle: Record<string, PspRole>;
}

/**
 * How a PSP frame declares which card field it holds, by its iframe `name`.
 * Braintree Hosted Fields serves EVERY field frame from the exact same file
 * (`https://assets.braintreegateway.com/.../hosted-fields-frame.html`), so the
 * role is NOT in the URL and NOT in the merchant-supplied `title`. Instead
 * Braintree encodes the field in the iframe `name`
 * (`braintree-hosted-field-number`, `-expirationDate`, `-cvv`, `-postalCode`,
 * …) and its own in-iframe code reads `window.name` to identify the field — so
 * the name is AUTHORITATIVE and readable SYMMETRICALLY in both contexts: the top
 * frame reads `iframe.name`, and the in-iframe instance reads `window.name`.
 * (Unlike `byFrameTitle`, where the merchant page carries a human `title` the
 * iframe itself cannot read.) `map` keys are **lowercased substrings** tested
 * against the (lowercased) frame name; the FIRST key that is a case-insensitive
 * substring of the name wins, mapping to its role. `map` values are untrusted
 * data: an unknown value resolves to `null`, never to code.
 */
export interface PspRoleByFrameName {
  byFrameName: Record<string, PspRole>;
}

/**
 * How a PSP frame declares which card field it holds — a discriminated union of
 * the supported strategies: `byQueryParam` (Gr4vy), `byFrameTitle` (Stripe), and
 * `byFrameName` (Braintree). A client switches on which key the (untrusted,
 * signed) object carries; a malformed object resolves no role.
 */
export type PspFieldRole = PspRoleByQueryParam | PspRoleByFrameTitle | PspRoleByFrameName;

/**
 * A signed allowlist entry describing one PSP card-field frame origin and how a
 * frame of that origin resolves which field it holds. `origin` is either an exact
 * origin (`https://js.stripe.com`) or a single-wildcard pattern where `*` stands
 * for exactly one DNS label (`https://secure-fields.*.gr4vy.app`). The matcher
 * (`pspAdapterForOrigin`) is a conservative string matcher, NOT a regex compiled
 * from the bundle — the `*` is the only metacharacter honoured.
 */
export interface PspAdapter {
  /** PSP identifier, e.g. `'gr4vy'`. Informational. */
  psp: string;
  /** Exact origin or a single-`*`-label wildcard origin pattern. */
  origin: string;
  role: PspFieldRole;
}

/** Per-site override rule. All fields optional; every field is inert data. */
export interface SiteRule {
  quirks?: SiteQuirk[];
  /** CSS selector strings that OVERRIDE heuristic field detection. Data, never code. */
  fields?: { username?: string[]; password?: string[] };
  /** Force the form classification (login / signup / change-password). */
  form?: SiteForm;
  changePasswordUrl?: string;
  /** Apple password-rules grammar, consumed by the generator (P3). */
  passwordRules?: string;
}

/** The full bundle — the on-disk shape (before the detached signature, P2). */
export interface SiteRulesBundle {
  format: typeof SITE_RULES_FORMAT;
  version: number;
  generatedAt: string;
  /** A bundle may require a client capability; older clients ignore rules gated above their version (P2). */
  minExtensionVersion?: string;
  equivalentDomains: EquivalentGroup[];
  sites: Record<string, SiteRule>;
  /**
   * Optional, forward-compatible allowlist of well-known cross-origin PSP
   * card-field frame origins (spec `2026-10-11-cross-origin-psp-card-autofill-
   * design.md` §4.1). Older clients ignore it — it is just an extra optional
   * field on the signed JSON, covered by the existing signature with no new
   * trust root. SHIPS EMPTY: no shipped snapshot carries it, so the cross-origin
   * PSP path is dormant until a signed bundle adds an entry.
   */
  pspAdapters?: PspAdapter[];
}

/**
 * The bundled baseline: today's hand-vetted equivalent-domains, no site rules.
 * Ships with every client so the feature degrades to current behavior offline /
 * before any signed bundle is fetched. Its `generatedAt` is the epoch so any real
 * fetched bundle (newer `generatedAt`) always supersedes it in P2's monotonic check.
 */
export const BASELINE_SITE_RULES: SiteRulesBundle = {
  format: SITE_RULES_FORMAT,
  version: SITE_RULES_VERSION,
  generatedAt: '1970-01-01T00:00:00.000Z',
  equivalentDomains: RELATED_DOMAIN_GROUPS.map((group) => ({ group: [...group], vetted: true })),
  sites: {},
};

// Per-bundle memo of the domain→group index (built from VETTED groups only), so
// repeated matching calls on the same bundle object don't rebuild it.
const indexCache = new WeakMap<SiteRulesBundle, Map<string, ReadonlySet<string>>>();

function vettedGroupIndex(bundle: SiteRulesBundle): Map<string, ReadonlySet<string>> {
  let index = indexCache.get(bundle);
  if (index) return index;
  index = new Map<string, ReadonlySet<string>>();
  for (const eg of bundle.equivalentDomains) {
    if (!eg.vetted) continue; // anti-phishing: only vetted groups ever match
    const set = new Set(eg.group.map((d) => d.toLowerCase()));
    for (const d of set) {
      // A domain can belong to more than one vetted group at once — e.g. the
      // remote-site-rules union floor (siteRules.ts store, P4) adopts a
      // fetched bundle's groups ALONGSIDE the shipped snapshot's, and the two
      // sources can each place the same domain in a differently-shaped group.
      // Union rather than overwrite: dropping an earlier group's members here
      // when a later group also touches `d` is the exact "concatenate and
      // rebuild the index, last group wins" bug the union floor exists to
      // prevent — just reintroduced one layer down. This does not invent a
      // wider transitive group (a bad merge of two touching-but-different
      // groups) because each domain's entry is still built only from groups
      // that directly contain THAT domain, never from groups that merely
      // touch another domain in its set.
      const existing = index.get(d);
      index.set(d, existing ? new Set([...existing, ...set]) : set);
    }
  }
  indexCache.set(bundle, index);
  return index;
}

/**
 * All domains in `domain`'s vetted group (including itself), or just
 * `[domain.toLowerCase()]` when it is ungrouped. The bundle-driven equivalent of
 * `relatedDomains()` in `relatedDomains.ts`.
 */
export function equivalentGroupsOf(bundle: SiteRulesBundle, domain: string): string[] {
  const set = vettedGroupIndex(bundle).get(domain.toLowerCase());
  return set ? [...set] : [domain.toLowerCase()];
}

/**
 * Symmetric: true when `a === b`, or `a` and `b` share a **vetted** group in the
 * bundle. Case-insensitive. The bundle-driven equivalent of `areRelatedDomains()`
 * — P2 swaps the extension matcher onto this, sourced from the fetched bundle.
 * The caller keeps the existing never-for-passkeys gate; this function is purely
 * the group lookup.
 */
export function areRelatedInBundle(bundle: SiteRulesBundle, a: string, b: string): boolean {
  const x = a.toLowerCase();
  const y = b.toLowerCase();
  if (x === y) return true;
  const set = vettedGroupIndex(bundle).get(x);
  return set ? set.has(y) : false;
}

/** The per-site rule for a registrable domain, or `undefined`. Case-insensitive. */
export function siteRuleFor(bundle: SiteRulesBundle, domain: string): SiteRule | undefined {
  return bundle.sites[domain.toLowerCase()];
}

/**
 * Conservative origin match for a PSP adapter pattern. `pattern` is either an
 * exact origin or a pattern containing a SINGLE `*` that stands for exactly one
 * DNS label (letters / digits / hyphen, non-empty, no dots, no slashes). Any
 * other `*` arrangement, or a `*` matching across a label boundary, fails.
 *
 * It is NOT a regex: the `*` is the only honoured metacharacter and the literal
 * parts are matched byte-for-byte via prefix/suffix, so a bundle author cannot
 * smuggle regex metacharacters, and a look-alike like
 * `https://secure-fields.evil.com` can never match `https://secure-fields.*.gr4vy.app`.
 */
function pspOriginMatches(pattern: string, origin: string): boolean {
  if (typeof pattern !== 'string' || typeof origin !== 'string' || !pattern || !origin) return false;
  const pat = pattern.toLowerCase();
  const org = origin.toLowerCase();
  const star = pat.indexOf('*');
  if (star === -1) return pat === org; // exact origin
  if (pat.indexOf('*', star + 1) !== -1) return false; // only a single wildcard is supported
  const prefix = pat.slice(0, star);
  const suffix = pat.slice(star + 1);
  if (!org.startsWith(prefix) || !org.endsWith(suffix)) return false;
  // Guard against prefix/suffix overlapping (which would make the slice below
  // wrap); the wildcard portion must be a real, separate span.
  if (org.length < prefix.length + suffix.length) return false;
  const middle = org.slice(prefix.length, org.length - suffix.length);
  // The wildcard stands for exactly ONE DNS label: non-empty, no `.` (so it
  // cannot span subdomains) and no `/` (so it cannot escape the origin).
  return /^[a-z0-9-]+$/.test(middle);
}

/**
 * The PSP adapter whose origin pattern matches `origin`, or `null`. This is the
 * allowlist-membership check and the sole security gate for releasing a PAN into
 * a cross-origin frame — the caller MUST pass the unspoofable `sender.url`
 * origin, never a page-supplied URL. Returns `null` for a bundle with no
 * `pspAdapters` (the shipped/dormant state), so no origin is ever allowlisted
 * until a signed bundle adds an entry.
 */
export function pspAdapterForOrigin(origin: string, bundle: SiteRulesBundle): PspAdapter | null {
  const adapters = bundle.pspAdapters;
  if (!Array.isArray(adapters) || !origin) return null;
  for (const adapter of adapters) {
    if (adapter && pspOriginMatches(adapter.origin, origin)) return adapter;
  }
  return null;
}

/**
 * Resolve which card field a PSP frame holds, from the frame's URL and the
 * adapter's `role` rule — the `byQueryParam` strategy (Gr4vy's `?type=`). Returns
 * `null` when the adapter's role is the `byFrameTitle` shape (a Stripe frame's
 * role is not in its URL; use {@link pspFieldRoleFromTitle} for that). The mapped
 * value is validated against the closed {@link PspRole} set — an unknown or
 * missing value resolves to `null`, never to an arbitrary string, so a malformed
 * signed bundle cannot invent a role. Defensive against a malformed `role`
 * object: the bundle is untrusted signed data.
 */
export function pspFieldRole(frameUrl: string, adapter: PspAdapter): PspRole | null {
  const role = adapter?.role as PspFieldRole | undefined;
  // Not the query-param shape (e.g. a `byFrameTitle` Stripe adapter) → no URL role.
  if (!role || typeof role !== 'object' || !('byQueryParam' in role)) return null;
  if (typeof role.byQueryParam !== 'string' || !role.map || typeof role.map !== 'object') {
    return null;
  }
  let raw: string | null;
  try {
    raw = new URL(frameUrl).searchParams.get(role.byQueryParam);
  } catch {
    return null;
  }
  if (raw === null) return null;
  const mapped = role.map[raw];
  return typeof mapped === 'string' && PSP_ROLES.has(mapped) ? mapped : null;
}

/**
 * Resolve which card field a PSP frame holds, from the merchant page's iframe
 * `title` and the adapter's `role` rule — the `byFrameTitle` strategy (Stripe
 * Elements). Returns `null` unless the adapter's role is the `byFrameTitle`
 * shape. Lowercases `title` and finds the FIRST map key that is a substring of
 * it, validating the mapped value against the closed {@link PspRole} set — an
 * unknown or missing value resolves to `null`, never to an arbitrary string, so
 * a malformed signed bundle cannot invent a role. Defensive against a malformed
 * `role` object: the bundle is untrusted signed data.
 */
export function pspFieldRoleFromTitle(title: string, adapter: PspAdapter): PspRole | null {
  const role = adapter?.role as PspFieldRole | undefined;
  // Not the frame-title shape (e.g. a `byQueryParam` Gr4vy adapter) → no title role.
  if (!role || typeof role !== 'object' || !('byFrameTitle' in role)) return null;
  const map = role.byFrameTitle;
  if (!map || typeof map !== 'object' || typeof title !== 'string') return null;
  const haystack = title.toLowerCase();
  for (const key of Object.keys(map)) {
    if (key && haystack.includes(key.toLowerCase())) {
      const mapped = map[key];
      return typeof mapped === 'string' && PSP_ROLES.has(mapped) ? mapped : null;
    }
  }
  return null;
}

/**
 * Resolve which card field a PSP frame holds, from the frame's `name` and the
 * adapter's `role` rule — the `byFrameName` strategy (Braintree Hosted Fields).
 * Returns `null` unless the adapter's role is the `byFrameName` shape. Lowercases
 * `name` and finds the FIRST map key that is a substring of it, validating the
 * mapped value against the closed {@link PspRole} set — an unknown or missing
 * value resolves to `null`, never to an arbitrary string, so a malformed signed
 * bundle cannot invent a role. Defensive against a malformed `role` object: the
 * bundle is untrusted signed data.
 *
 * Unlike {@link pspFieldRoleFromTitle} (whose `title` only the merchant page can
 * read), the iframe `name` is readable in BOTH frames — the top frame reads
 * `iframe.name`, the in-iframe instance reads `window.name` — so a single resolver
 * serves both halves of the Braintree path.
 */
export function pspFieldRoleFromName(name: string, adapter: PspAdapter): PspRole | null {
  const role = adapter?.role as PspFieldRole | undefined;
  // Not the frame-name shape (e.g. a `byQueryParam`/`byFrameTitle` adapter) → no name role.
  if (!role || typeof role !== 'object' || !('byFrameName' in role)) return null;
  const map = role.byFrameName;
  if (!map || typeof map !== 'object' || typeof name !== 'string') return null;
  const haystack = name.toLowerCase();
  for (const key of Object.keys(map)) {
    if (key && haystack.includes(key.toLowerCase())) {
      const mapped = map[key];
      return typeof mapped === 'string' && PSP_ROLES.has(mapped) ? mapped : null;
    }
  }
  return null;
}
